import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as UploadScreen } from "../../../../assets/icons/svg/add-screen-image.svg";
import { ReactComponent as ScreenUpload } from "../../../../assets/icons/svg/add-screen.svg";
import { ReactComponent as AddTag } from "../../../../assets/icons/svg/add-tag.svg";
import { ReactComponent as AddGroup } from "../../../../assets/icons/svg/add-folder.svg";
import { useScreens } from "../../../../context/ScreenContext";
import { useTopBar } from "../../../../context/TopBarContext";
import { useToast, ToastType } from "../../../ui/Toast_Message";
import ToastContainer from "../../../ui/Toast_Message";
import ScreenUploadPopup from "./ScreenUploadPopup";
import TagManagementPopup from "./TagManagementPopup";
import DeletePopup from "../../../ui/DeletePopup";
import AddGroupPopup from "./AddGroupPopup";
import { useGroups } from "../../../../context/GroupContext";
import { useTags, Tag, SelectedTag } from "../../../../context/TagContext";

const UploadScreenComponent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const shouldOpenDirectly = searchParams.get("direct") === "true";

  const [showPopup, setShowPopup] = useState(shouldOpenDirectly);
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [showAddGroupPopup, setShowAddGroupPopup] = useState(false);
  const [deletePopup, setDeletePopup] = useState<{
    show: boolean;
    tagId: string | null;
  }>({
    show: false,
    tagId: null,
  });

  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const { showToast, toasts } = useToast();
  const { screens, addScreen } = useScreens();
  const { availableTags, addTag, updateTag, deleteTag, setAvailableTags } =
    useTags();
  const { groups, addGroup } = useGroups();

  useEffect(() => {
    setTitle("Screens");
    setShowBackButton(false);
    pushNavState({
      path: location.pathname,
      title: "Screens",
      showBackButton: false,
    });
  }, [location.pathname, setTitle, setShowBackButton, pushNavState]);

  // Extract unique tags from screens
  useEffect(() => {
    const extractedTags = new Map<string, Tag>();
    screens.forEach((screen: any) => {
      if (screen.tags) {
        screen.tags.forEach((tag: SelectedTag) => {
          if (!extractedTags.has(tag.tag.toLowerCase())) {
            extractedTags.set(tag.tag.toLowerCase(), {
              id: `extracted-${tag.tag.toLowerCase().replace(/\s+/g, "-")}`,
              name: tag.tag,
            });
          }
        });
      }
    });

    setAvailableTags((currentTags) => {
      const existingTagNames = new Set(
        currentTags.map((tag) => tag.name.toLowerCase())
      );
      const newTags = Array.from(extractedTags.values()).filter(
        (tag) => !existingTagNames.has(tag.name.toLowerCase())
      );
      return [...currentTags, ...newTags];
    });
  }, [screens, setAvailableTags]);

  const showToastWrapper = (type: string, title: string, message: string) => {
    showToast(type as ToastType, title, message);
  };

  const handleAddTagPopup = () => setShowTagPopup(true);

  const handleSaveTag = (tagName: string): boolean => {
    const success = addTag(tagName);
    if (!success) {
      const tagExists = availableTags.some(
        (tag) => tag.name.toLowerCase() === tagName.toLowerCase()
      );
      if (tagExists) {
        showToast(
          "error",
          "Duplicate Tag",
          "A tag with this name already exists"
        );
      } else if (availableTags.length >= 10) {
        showToast(
          "error",
          "Maximum Limit",
          "You can only create up to 10 tags"
        );
      }
      return false;
    }
    showToast("success", "Tag Created", "Tag has been created successfully");
    return true;
  };

  const handleUpdateTag = (
    tagId: string,
    newName: string,
    selectedTags: SelectedTag[]
  ) => {
    const success = updateTag(tagId, newName);
    if (!success) {
      showToast(
        "error",
        "Duplicate Tag",
        "A tag with this name already exists"
      );
      return { success: false, updatedSelectedTags: selectedTags };
    }

    const oldTag = availableTags.find((tag) => tag.id === tagId);
    if (oldTag) {
      screens.forEach((screen: any) => {
        if (screen.tags?.some((tag: SelectedTag) => tag.tag === oldTag.name)) {
          const updatedTags = screen.tags.map((tag: SelectedTag) =>
            tag.tag === oldTag.name ? { ...tag, tag: newName.trim() } : tag
          );
          // updateScreen(screen.id, { tags: updatedTags });
        }
      });
    }

    showToast("success", "Tag Updated", "Tag has been updated successfully");
    return { success: true, updatedSelectedTags: selectedTags };
  };

  const handleDeleteTag = (tagId: string) => {
    setDeletePopup({ show: true, tagId });
  };

  const handleSaveGroup = (groupName: string): boolean => {
    const success = addGroup(groupName);
    if (!success) {
      showToast(
        "error",
        "Duplicate Group",
        "A group with this name already exists"
      );
      return false;
    }

    const newGroup = groups.find((g) => g.name === groupName);
    showToast(
      "success",
      "Group Created",
      "Group has been created successfully"
    );

    // Navigate to ScreensListComponent with the new group ID
    navigate("/screens/list", {
      // state: { newlyCreatedGroupId: newGroup?.id || null },
    });
    return true;
  };

  const confirmDeleteTag = () => {
    if (deletePopup.tagId) {
      const tagToDelete = availableTags.find((t) => t.id === deletePopup.tagId);
      const isTagInUse = screens.some((screen: any) =>
        screen.tags?.some((tag: SelectedTag) => tag.tag === tagToDelete?.name)
      );
      deleteTag(deletePopup.tagId);
      showToast("success", "Tag Deleted", "Tag has been deleted successfully");
    }
    setDeletePopup({ show: false, tagId: null });
  };

  const cancelDeleteTag = () => setDeletePopup({ show: false, tagId: null });

  const handleUpload = (screenData: {
    name: string;
    group: string;
    orientation: string;
    tags: SelectedTag[];
  }) => {
    if (screenData.group !== "") {
      const groupExists = groups.some((group) => group.id === screenData.group);
      if (!groupExists) {
        showToast(
          "error",
          "Invalid Group",
          "The selected group does not exist. Please select a valid group."
        );
        return;
      }
    }

    addScreen({
      name: screenData.name,
      group: screenData.group,
      orientation: screenData.orientation,
      tags: screenData.tags,
      status: "offline",
    });

    showToast("success", "Screen Added", "Screen has been added successfully!");
    setShowPopup(false);
    navigate("/screens/list");
  };

  const isAnyPopupOpen =
    showPopup || showTagPopup || deletePopup.show || showAddGroupPopup;

  return (
    <>
      <div
        className={`fixed inset-20  overflow-hidden flex items-center justify-center bg-white mt-1 transition-opacity duration-200 ${
          isAnyPopupOpen ? "opacity-30 pointer-events-none" : "opacity-100"
        }`}
      >
        <div className="fixed top-6  right-6 z-30 2xl:z-60 flex gap-3 mt-16 2xl:mt-28">
          <button
            onClick={() => setShowAddGroupPopup(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-[#0084A4] rounded-[8px] text-xs 2xl:text-base font-medium"
          >
            <AddGroup className="w-5 h-5 3xl:w-6 3xl:h-6" /> Add new group
          </button>
          <button
            onClick={handleAddTagPopup}
            className="inline-flex items-center gap-2 px-4 py-2 h-[32px] md:h-[32px] lg:h-[46px] xl:h-[42px] 2xl:h-[46px] 3xl:h-[52px] border border-[#0084A4] bg-[#F2FAFD] text-[#0084A4] rounded-[10px] text-xs 2xl:text-base font-medium"
          >
            <AddTag className="w-5 h-5 3xl:w-6 3xl:h-6" /> Update tags
          </button>
        </div>
        <div className="flex flex-col items-center text-center space-y-4 max-w-md">
          <UploadScreen className="w-52 h-52 -mb-8" />
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-[#1F1F1F]">
              Add Screen
            </h2>
            <p className="text-sm text-[#808191F5]">
              Screens help you organize and manage your display devices for
              content distribution.
            </p>
          </div>
          <button
            onClick={() => setShowPopup(true)}
            className="inline-flex items-center justify-center gap-2.5 w-48 h-12 px-4 bg-[#0BA6C7] hover:bg-[#0A99B9] text-white text-sm font-medium border border-[#0BA6C7] hover:border-[#0A99B9] rounded-xl transition-colors duration-200 active:bg-[#0991A8]"
          >
            <ScreenUpload className="w-6 h-6" /> Add Screen
          </button>
        </div>
      </div>

      {showPopup && (
        <ScreenUploadPopup
          onClose={() => setShowPopup(false)}
          onUpload={handleUpload}
          showToast={showToastWrapper}
        />
      )}

      {showTagPopup && (
        <TagManagementPopup
          onClose={() => setShowTagPopup(false)}
          showToast={showToastWrapper}
        />
      )}

      {deletePopup.show && (
        <DeletePopup
          title="Delete Tag"
          message="Are you sure you want to delete this tag? It will also be removed from any screens that are using it."
          onCancel={cancelDeleteTag}
          onConfirm={confirmDeleteTag}
        />
      )}

      {showAddGroupPopup && (
        <AddGroupPopup
          onClose={() => setShowAddGroupPopup(false)}
          onSave={handleSaveGroup}
        />
      )}

      <ToastContainer toasts={toasts} />
    </>
  );
};

export default UploadScreenComponent;
